import { ExperienceIcon } from "assets/icons/DetailedProfile";
import React, { useContext, useEffect, useState } from "react";
import { SectionTitle } from "../../Shared";
import { SingleExperience } from "./Index";
import { Button } from "antd";
import DELETE_ICON from "assets/icons/DetailedProfile/delete.svg";
import editPencil from "assets/icons/profileIcons/editPencil.svg";
import _ from "lodash";
import { EditContext } from "../../Context/EditContext";
import { PlusOutlined } from "@ant-design/icons";

interface PropsType {
  candidate: any;
  nocomments?: any;
  isRecruiterPage?: boolean;
  handleUpdate?: any;
  handleDeleteItem?: any;
  setDisplayExperienceForm?: any;
  isEditing?: any;
}

const Experience: React.FC<PropsType> = ({
  candidate,
  nocomments,
  isRecruiterPage,
  handleUpdate,
  handleDeleteItem,
  setDisplayExperienceForm,
  isEditing,
}) => {
  const [exp, setExp] = useState([]);
  const [data, setData] = useState([]);
  const editContext = useContext<any>(EditContext);
  if (!editContext?.edit) {
    candidate?.experiences?.sort((a: any, b: any): any => {
      if (a.current && !b.current) {
        return -1; // a comes before b
      } else if (!a.current && b.current) {
        return 1; // b comes before a
      } else if (a.current && b.current) {
        return b.endYear - a.endYear; // sort by descending endYear
      } else {
        return b.endYear - a.endYear; // sort by descending endYear
      }
    });
  }

  const Styles = {
    root: {
      padding: isRecruiterPage ? 0 : 15,
      boxShadow: isRecruiterPage ? "unset" : "0px 0px 4px #00000040 ",
      borderRadius: 10,
      paddingBottom: 5,
    },
    body: {
      marginTop: isRecruiterPage ? "32px" : "20px",
    },
  };

  function mergeAndSort(arr: any) {
    if (!arr || arr.length === 0) return [];

    // Check if any experience has groupId
    const hasAnyGroupId = arr.some((exp: any) => exp.groupId);

    if (!hasAnyGroupId) {
      // Legacy behavior: group by company name
      const groupedExperiences: any = {};
      const ungroupedExperiences: any = [];

      for (let i = 0; i < arr?.length; i++) {
        const experience = arr[i];
        const companyName =
          experience.company?.toLowerCase().replace(/\s+/g, "_") || "unknown";

        if (!groupedExperiences[companyName]) {
          groupedExperiences[companyName] = [];
        }
        groupedExperiences[companyName].push(experience);
      }

      const result: any = [];

      // Process grouped experiences
      Object.keys(groupedExperiences).forEach((companyName) => {
        const group = groupedExperiences[companyName];
        if (group.length > 1) {
          // Create merged experience with the first experience as base
          const mergedExperience = { ...group[0] };
          mergedExperience.mergedData = [...group];

          // Sort merged experiences by start/end year (only in view mode)
          if (!editContext?.edit) {
            mergedExperience.mergedData.sort((a: any, b: any) => {
              if (a.startYear !== b.startYear) {
                return b.startYear - a.startYear;
              } else {
                return b.endYear - a.endYear;
              }
            });
          }

          result.push(mergedExperience);
        } else {
          // Single experience in group, add as-is
          result.push(group[0]);
        }
      });

      // Add ungrouped experiences
      result.push(...ungroupedExperiences);

      return result;
    } else {
      // New behavior: group by groupId
      const groupedExperiences: any = {};
      const ungroupedExperiences: any = [];

      for (let i = 0; i < arr?.length; i++) {
        const experience = arr[i];
        const groupId = experience.groupId;

        if (groupId) {
          if (!groupedExperiences[groupId]) {
            groupedExperiences[groupId] = [];
          }
          groupedExperiences[groupId].push(experience);
        } else {
          // Experiences without groupId remain individual
          ungroupedExperiences.push(experience);
        }
      }

      const result: any = [];

      // Process grouped experiences
      Object.keys(groupedExperiences).forEach((groupId) => {
        const group = groupedExperiences[groupId];
        if (group.length > 1) {
          // Create merged experience with the first experience as base
          const mergedExperience = { ...group[0] };
          mergedExperience.mergedData = [...group];

          // Sort merged experiences by start/end year (only in view mode)
          if (!editContext?.edit) {
            mergedExperience.mergedData.sort((a: any, b: any) => {
              if (a.startYear !== b.startYear) {
                return b.startYear - a.startYear;
              } else {
                return b.endYear - a.endYear;
              }
            });
          }

          result.push(mergedExperience);
        } else {
          // Single experience in group, add as-is
          result.push(group[0]);
        }
      });

      // Add ungrouped experiences
      result.push(...ungroupedExperiences);

      return result;
    }
  }

  useEffect(() => {
    // Use updated data from edit context when in edit mode, otherwise use candidate data
    const experiencesData = editContext?.edit
      ? editContext?.updatedData?.experiences ||
        editContext?.candidate?.experiences ||
        candidate?.experiences
      : candidate?.experiences;

    const exp = _.cloneDeep(experiencesData);

    // If entering edit mode and no experiences have groupId, assign groupId based on company name
    if (editContext?.edit && exp && exp.length > 0) {
      const hasAnyGroupId = exp.some((experience: any) => experience.groupId);

      if (!hasAnyGroupId) {
        const companyGroups: any = {};

        exp.forEach((experience: any) => {
          const companyName =
            experience.company?.toLowerCase().replace(/\s+/g, "_") || "unknown";
          if (!companyGroups[companyName]) {
            companyGroups[companyName] = `group_${companyName}_${Date.now()}`;
          }
          experience.groupId = companyGroups[companyName];
        });

        // Update the edit context with the new groupId assignments
        if (editContext?.updatedData?.experiences) {
          editContext.setUpdatedData({
            ...editContext.updatedData,
            experiences: exp,
          });
        } else {
          editContext.setUpdatedData({
            ...editContext.updatedData,
            experiences: exp,
          });
        }

        editContext.setData({
          ...editContext.candidate,
          experiences: exp,
        });
      }
    }

    setExp(exp);
  }, [
    candidate?.experiences,
    editContext?.updatedData?.experiences,
    editContext?.edit,
  ]);

  useEffect(() => {
    setData(mergeAndSort(exp));
  }, [exp, editContext?.edit]);

  const handleClick = (experience: any) => {
    handleUpdate({ ...experience });
    setDisplayExperienceForm(true);
  };

  const handleDelete = (exp: any) => {
    handleDeleteItem(exp);
  };

  const addNew = () => {
    let exp = editContext?.updatedData?.experiences?.length
      ? editContext?.updatedData?.experiences
      : editContext?.candidate?.experiences;

    // Check if any experience has groupId
    const hasAnyGroupId = exp.some((experience: any) => experience.groupId);

    if (hasAnyGroupId) {
      // New behavior: create experience with unique groupId (independent)
      const newExperience = {
        _id: Math.random().toString(),
        groupId: `independent_${Date.now()}_${Math.random()}`, // Unique groupId to prevent grouping
      };

      exp = [...exp, newExperience];
    } else {
      // Legacy behavior: create experience without groupId (will be grouped by company name)
      const newExperience = {
        _id: Math.random().toString(),
        // No groupId - will be grouped by company name in view mode
      };

      exp = [...exp, newExperience];
    }

    editContext?.setUpdatedData({
      ...editContext?.updatedData,
      experiences: exp,
    });
    editContext?.setData({
      ...editContext?.candidate,
      experiences: exp,
    });
  };

  return (
    <>
      {((candidate?.experiences != null &&
        candidate?.experiences.length != 0 &&
        candidate?.experiences[0] != null) ||
        editContext?.edit) && (
        <div style={isRecruiterPage ? {} : Styles.root}>
          <SectionTitle
            showstyle={false}
            title={<span>Experience</span>}
            fontSize="16px"
          />
          <div style={Styles.body}>
            {data &&
              data.map((experience: any, i: any) => {
                return (
                  <div
                    key={i}
                    style={{
                      marginBottom: "18px",
                      display: "flex",
                      justifyContent: "space-between",
                    }}
                  >
                    <div style={{ width: "100%" }}>
                      <SingleExperience
                        experience={experience}
                        candidateId={candidate?._id}
                      />
                      {/* {nocomments === false ? (
                        <></>
                      ) : (
                        <>
                          <ExperienceComment experience={experience} />
                        </>
                      )}    */}
                    </div>
                  </div>
                );
              })}
            {editContext?.edit && (
              <Button
                style={{
                  maxWidth: "233px",
                  marginBottom: "8px",
                  borderRadius: "15px",
                }}
                type="dashed"
                onClick={addNew}
                block
              >
                Add a new experience <PlusOutlined />
              </Button>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default Experience;
